import React from 'react'
import classNames from 'classnames'
import { PricingCardCTAProps } from './types'
import Typography from '@toasttab/shared/src/components/atoms/Typography/Typography'
import Button from '@toasttab/shared/src/components/atoms/Button/Button'
import Badge from '@toasttab/shared/src/components/atoms/Badge/Badge'
import StyledList from '@toasttab/shared/src/components/molecules/StyledList/StyledList'
import TextLink from '@toasttab/shared/src/components/molecules/TextLink/TextLink'
import Image from '../../atoms/Image/Image'
import { Icon } from '@toasttab/shared/src/utils/craftUtils'
import ImageResolutions from '../../atoms/Image/ImageResolutions'
import { ContentfulImageAPI } from '../../../utils/contentfulUtils'

const PricingCardCTA = ({
  accentColor,
  accentPosition,
  badgeColor,
  badgeText,
  children, // styledList
  ctaLabel,
  ctaLink,
  padding,
  packagePrice,
  packagePriceMonthly = '',
  pricePrefix,
  priceSuffix,
  priceStrikethrough,
  sectionDescription,
  sectionHeading,
  image,
  bottomLink,
  textAlignment,
  fourCardLayout = false,
  dataTestId = 'PricingCardCTA-1'
}: PricingCardCTAProps) => {
  const pricingCardClasses = classNames({
    'inline-grid box-border gap-y-4 !p-6': true,
    'grid-rows[minmax(auto,272px)_auto] md:grid-rows-[minmax(auto,252px)_auto]':
      packagePrice && packagePrice.trim() != '',
    'lg:grid-rows-[minmax(auto,310px)_auto]': fourCardLayout
  })
  const renderImage = () => {
    if (image) {
      let query = ContentfulImageAPI.transformImageAvif({
        width: 900,
        extension: image.file?.contentType
      })
      let srcsetWeb = ContentfulImageAPI.transformImageAvifSrcset({
        url: image.file?.url,
        widths: [900, 736, 393],
        extension: image.file?.contentType
      })
      const imgResolutions = {
        imageResolutions: [
          {
            borderRadius: 'Small',
            desktop: [
              {
                optimizedImages: {
                  src: image.file?.url,
                  srcsetAvif: srcsetWeb ? srcsetWeb : image.file?.url,
                  srcAvif: image.file?.url + query
                },
                url: image.file?.url,
                title: image.title,
                extension: image.file?.contentType
              }
            ]
          }
        ]
      }
      return (
        <div className='w-full'>
          <ImageResolutions images={imgResolutions} />
        </div>
      )
    }
  }

  return (
    <div
      className='bg-white mt-[26px] relative rounded-md border-card-light pricing-card'
      data-testid={dataTestId}
    >
      <div className='ds-grid gap-y-0 p-6'>
        <div className='col-span-4'>
          {badgeText && badgeColor && (
            <div className='mb-2'>
              <Badge badgeColor={badgeColor} text={badgeText} uppercase />
            </div>
          )}
          <div>
            {sectionHeading && (
              <Typography
                variation='h4'
                as='h4'
                classes='mb-[10px] sm:mb-4 !font-normal'
              >
                {sectionHeading}
              </Typography>
            )}
            {sectionDescription && (
              <Typography variation='default' as='p' classes='text-secondary'>
                {sectionDescription}
              </Typography>
            )}
          </div>
          <div className='mt-4 self-center'>
            {pricePrefix && (
              <Typography as='p' variation='default' classes='text-secondary'>
                {pricePrefix}
              </Typography>
            )}
            {priceStrikethrough && (
              <Typography
                as='h3'
                variation='h4'
                classes='text-gray-50 line-through !font-normal'
              >
                {priceStrikethrough}
              </Typography>
            )}
            {packagePrice && (
              <p className='text-[31px] leading-[40px] text-default !font-effra'>
                {packagePrice}
                {packagePriceMonthly && (
                  <Typography
                    as='span'
                    variation='default'
                    classes='text-secondary'
                  >
                    {packagePriceMonthly}
                  </Typography>
                )}
              </p>
            )}
            {priceSuffix && (
              <Typography as='p' variation='default' classes='text-secondary'>
                {priceSuffix}
              </Typography>
            )}
          </div>
          <div className='mt-4 sm:mt-6'>
            {ctaLabel && ctaLink && (
              <Button
                href={ctaLink}
                variation='primary'
                fontWeight={['font-semibold']}
                size='medium'
              >
                {ctaLabel}
              </Button>
            )}
          </div>
        </div>
        <div className='col-span-4 mt-4 sm:mt-0'>
          {children &&
            children.map((list: any, index: number) => {
              return (
                <StyledList
                  key={index}
                  sectionHeading={list.sectionHeading}
                  sectionDescription={list.sectionDescription}
                  listStyles={list.listStyles}
                  listItems={list.listItems}
                  listVariation='default'
                  classes='[&:not(:first-child)]:mt-2 sm:[&:not(:first-child)]:mt-4'
                />
              )
            })}
          {bottomLink && (
            <div className='mt-5 pt-2 border-t border-t-gray-30'>
              <TextLink
                href={bottomLink.url}
                text={bottomLink.label}
                icon={Icon.linkIcons(bottomLink.icon)}
                classes='mt-2'
              ></TextLink>
            </div>
          )}
        </div>
        <div className='col-span-4 content-center mt-[31px] sm:mt-0 mb-5 sm:mb-0'>
          {image && renderImage()}
        </div>
      </div>
    </div>
  )
}

export default PricingCardCTA
